import React, { FC, useState, useEffect, ChangeEvent, FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { Select, ISelectOption } from 'components/forms/Selects'
import { Button } from 'transfix-ui/components/Button'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Table, TableHead, TableRow, TableCell, TableBody } from 'transfix-ui/components/Table'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useGetCarriers, GetCarriersQueryParams } from 'apps/marketplace/apiTypes/carriers'
import { Pagination } from 'components/Pagination'
import { TextInput } from 'components/atoms'
import { carrierStatusOptions } from 'constants/carriers'
import { usStatesOptions } from 'constants/countriesStates'
import CarrierRow from './CarrierRow'
import { getOptionsWithNewCarrierStateLabels } from './utils'

const CarriersList: FC = () => {
  const [filterParams, setFilterParams] = useState<GetCarriersQueryParams>({
    companyInfo: '',
    status: '',
    dotNumber: '',
    mcNumber: '',
    city: '',
    stateCode: '',
    miles: '',
    pageCount: 0,
    pageSize: 25,
  })

  interface IState extends Partial<GetCarriersQueryParams> {}

  const [queryState, setQueryState] = useState<IState>({
    status: filterParams.status,
    companyInfo: '',
    dotNumber: '',
    mcNumber: '',
    city: '',
    stateCode: '',
    miles: '',
  })

  const isUseNewStateTagsMpEnabled = useFeatureFlag('useNewStateTagsMp')

  /**
   * @example setQueryValue('miles', '1000')
   */
  function setQueryValue<K extends keyof IState, T extends IState[K]>(keyName: K, value: T) {
    setQueryState(prev => ({
      ...prev,
      [keyName]: value,
    }))
  }

  const { data, error, loading, refetch } = useGetCarriers({
    queryParams: filterParams,
    lazy: true,
  })

  /**
   * when these variables update, re-run the fetch
   * */
  useEffect(() => {
    refetch()
    window.scrollTo(0, 0)
  }, [
    filterParams.companyInfo,
    filterParams.status,
    filterParams.dotNumber,
    filterParams.mcNumber,
    filterParams.city,
    filterParams.stateCode,
    filterParams.miles,
    filterParams.pageCount,
  ])

  const tableSpinner = () => (
    <TableRow>
      <TableCell colSpan={9} className='tc'>
        <div className='tc'>
          <div className='pa6'>
            <Spinner position='relative' />
          </div>
        </div>
      </TableCell>
    </TableRow>
  )

  const refreshPage = () => global.location.reload()
  const tableError = () => (
    <TableRow>
      <TableCell colSpan={9} className='tc'>
        <div className='tc'>
          <div className='pa6'>
            <div className='mb4'>
              Sorry, there was an issue loading results. Please try refreshing your page.
            </div>
            <button className='btn-text-link' onClick={refreshPage}>
              Refresh your page
            </button>
          </div>
        </div>
      </TableCell>
    </TableRow>
  )

  const tableEmpty = () => (
    <TableRow>
      <TableCell colSpan={9} className='tc'>
        <div className='tc'>
          <div className='pa6'>
            <div className='mb4'>No results found for this search.</div>
          </div>
        </div>
      </TableCell>
    </TableRow>
  )

  const carrierRows = () =>
    data?.carriers.map(carrier => <CarrierRow carrier={carrier} key={carrier.id} />)

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setFilterParams(currentState => ({
      ...currentState,
      pageCount: 0,
      companyInfo: queryState.companyInfo,
      status: queryState.status,
      dotNumber: queryState.dotNumber,
      mcNumber: queryState.mcNumber,
      city: queryState.city,
      stateCode: queryState.stateCode,
      miles: queryState.miles,
    }))
  }

  const statusHandler = (selection: ISelectOption<string>) => {
    return setQueryValue('status', selection.value)
  }

  const stateCodeHandler = (selection: ISelectOption<string>) => {
    return setQueryValue('stateCode', selection.value)
  }

  const handleCompanyInfoChange = (e: ChangeEvent<HTMLInputElement>) =>
    setQueryValue('companyInfo', e.target.value)
  const handleMcNumberChange = (e: ChangeEvent<HTMLInputElement>) =>
    setQueryValue('mcNumber', e.target.value)
  const handleDotNumberChange = (e: ChangeEvent<HTMLInputElement>) =>
    setQueryValue('dotNumber', e.target.value)
  const handleCityChange = (e: ChangeEvent<HTMLInputElement>) =>
    setQueryValue('city', e.target.value)
  const handleMilesChange = (e: ChangeEvent<HTMLInputElement>) =>
    setQueryValue('miles', e.target.value)

  const handleClear = (e: { preventDefault: () => void }) => {
    e.preventDefault()

    setQueryValue('companyInfo', '')
    setQueryValue('status', '')
    setQueryValue('dotNumber', '')
    setQueryValue('mcNumber', '')
    setQueryValue('city', '')
    setQueryValue('stateCode', '')
    setQueryValue('miles', '')
  }

  const onPageChange = (page: { pageCount: number }) => {
    return setFilterParams(currentState => ({
      ...currentState,
      pageCount: page.pageCount,
    }))
  }

  const stickyStyle = { top: '45px', zIndex: 2 }

  const optionsWithNewCarrierStateLabels = getOptionsWithNewCarrierStateLabels(
    carrierStatusOptions,
    isUseNewStateTagsMpEnabled
  )

  return (
    <>
      <div className='flex justify-between'>
        <form onSubmit={handleSubmit} className='mt4 flex w-80'>
          <span className='w-15 mr2'>
            <Select
              options={optionsWithNewCarrierStateLabels}
              value={queryState.status}
              onChange={statusHandler}
              placeholder='Status'
            />
          </span>
          <span className='w-20 mr4'>
            <TextInput
              placeholder='Name'
              value={queryState.companyInfo}
              onChange={handleCompanyInfoChange}
            />
          </span>
          <span className='w-10 mr4'>
            <TextInput
              placeholder='MC #'
              value={queryState.mcNumber}
              onChange={handleMcNumberChange}
            />
          </span>
          <span className='w-10 mr4'>
            <TextInput
              placeholder='DOT #'
              value={queryState.dotNumber}
              onChange={handleDotNumberChange}
            />
          </span>
          <span className='w-10 mr4'>
            <TextInput placeholder='City' value={queryState.city} onChange={handleCityChange} />
          </span>
          <span className='w-5 mr4'>
            <Select
              placeholder='ST'
              options={usStatesOptions}
              value={queryState.stateCode}
              onChange={stateCodeHandler}
            />
          </span>
          <span className='w-5 mr4'>
            <TextInput placeholder='Radius' value={queryState.miles} onChange={handleMilesChange} />
          </span>
          <span className='w-20 mr4'>
            <Button type='submit' color='primary' fullWidth size='small'>
              Search
            </Button>
          </span>
          <span className='w-5 mr4'>
            <Button color='primary' fullWidth size='small' onClick={handleClear}>
              Clear
            </Button>
          </span>
        </form>
        <div>
          <Link to='/new'>
            <Button>Create Carrier</Button>
          </Link>
        </div>
      </div>
      <section className='mt4'>
        <Table stickyHeader stickyHeaderStyle={stickyStyle}>
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>City, State</TableCell>
              <TableCell>Qualification</TableCell>
              <TableCell>Carrier Manager</TableCell>
              <TableCell>Safety</TableCell>
              <TableCell>Shipments</TableCell>
              <TableCell>Latest Note</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {error && tableError()}
            {loading && tableSpinner()}
            {!loading && data?.carriers?.length === 0 && tableEmpty()}
            {!loading && carrierRows()}
          </TableBody>
        </Table>
      </section>
      <div className='tc'>
        <Pagination
          currentPage={filterParams.pageCount}
          hasNextPage={data?.nextPage}
          onClickHandler={onPageChange}
        />
      </div>
    </>
  )
}

export default CarriersList
